/* ==============================================================================
   Detecção de fala por participante
   ==============================================================================
   Quem está falando agora, para cada par da malha. É o que permite o anel no
   card, o realce na lista, e o pedido que originou este arquivo: na tela cheia,
   os avatares dos participantes ficam translúcidos e o de quem fala fica opaco.

   Até aqui `speaking` era `false` FIXO em toda a interface. O campo existia no
   tipo, a UI já sabia desenhar o anel — e nada nunca o preenchia.

   Por que `getSynchronizationSources()` e não Web Audio
   ====================================================
   O caminho intuitivo seria criar um `AnalyserNode` por par e medir o RMS. Ele
   funciona e é o que a maioria dos tutoriais mostra. O custo é o problema:

     · um `MediaStreamSource` + `AnalyserNode` por par significa, numa sala de
       oito, SETE grafos de áudio rodando em paralelo — no mesmo thread que já
       está processando o próprio microfone pela cadeia de RNNoise;
     · o áudio precisa ser DECODIFICADO para ser analisado, então o custo cresce
       com o número de pares exatamente quando a CPU já está codificando vídeo
       para todos eles.

   `RTCRtpReceiver.getSynchronizationSources()` devolve, para cada fonte RTP
   recebida, o `audioLevel` que o REMETENTE calculou e enviou no cabeçalho da
   extensão RTP (RFC 6464). Ou seja:

     · o trabalho já foi feito por quem falou;
     · nada é decodificado para ler o número;
     · é uma chamada síncrona que devolve um array pequeno.

   O preço: o valor vem de quem envia, então um cliente malicioso poderia mentir
   sobre o próprio nível. Para "acender uma borda" isso não é um vetor que
   importe — o pior que se consegue é parecer que está falando.

   Por que HISTERESE e não um limiar só
   ====================================
   Fala humana tem pausas curtas entre palavras e sílabas. Um limiar único faz o
   indicador piscar várias vezes por segundo durante uma frase normal — o que é
   pior que não ter indicador, porque a piscada chama mais atenção que a
   informação.

   Dois limiares e um tempo de retenção resolvem: sobe fácil, desce devagar.
   ============================================================================== */

/**
 * Nível a partir do qual se considera que começou a falar.
 *
 * `audioLevel` vem normalizado de 0 a 1 pela especificação, derivado do valor em
 * dBov do cabeçalho RTP. 0,02 é aproximadamente −34 dBov: acima de ruído de
 * ambiente e de respiração, abaixo de fala normal com folga.
 */
const START_THRESHOLD = 0.02;

/**
 * Nível abaixo do qual se considera que parou — mais baixo que o de início.
 *
 * A diferença entre os dois é a histerese. Com um valor só, o indicador piscaria
 * a cada sílaba.
 */
const STOP_THRESHOLD = 0.008;

/**
 * Quanto tempo o indicador fica aceso depois do último nível alto.
 *
 * 250 ms cobre a pausa entre palavras de uma frase e o silêncio de uma consoante
 * oclusiva, sem deixar o indicador aceso depois de a pessoa terminar de falar.
 */
const HOLD_MS = 250;

interface SpeakerState {
  speaking: boolean;
  /** Último instante em que o nível passou de `START_THRESHOLD`. */
  lastLoudAt: number;
  /** Último nível lido, para a interface poder desenhar intensidade. */
  level: number;
}

export interface SpeakingSample {
  sessionId: string;
  speaking: boolean;
  /** 0..1. Útil para intensidade de brilho, não só liga/desliga. */
  level: number;
}

/**
 * Acompanha quem está falando, por sessão.
 *
 * Uma instância por chamada. `sample()` é chamado num intervalo (10 Hz é o
 * suficiente — a percepção de "acendeu junto com a voz" satura por volta disso)
 * e devolve apenas o que MUDOU, para não forçar re-render sem motivo.
 */
export class SpeakingDetector {
  private readonly state = new Map<string, SpeakerState>();

  /**
   * Lê o nível de cada par e devolve as mudanças.
   *
   * `receivers` é uma lista de `[sessionId, receiver]`. O chamador é quem sabe
   * qual receiver pertence a quem — este módulo não conhece a malha.
   *
   * `now` entra por parâmetro em vez de `Date.now()` interno para o teste poder
   * controlar o tempo. Histerese com relógio real é intestável: verificar que o
   * indicador fica aceso 250 ms exigiria esperar 250 ms de verdade.
   */
  sample(
    receivers: ReadonlyArray<readonly [string, RTCRtpReceiver]>,
    now: number,
  ): SpeakingSample[] {
    const changes: SpeakingSample[] = [];
    const seen = new Set<string>();

    for (const [sessionId, receiver] of receivers) {
      seen.add(sessionId);

      const level = readLevel(receiver);
      const previous = this.state.get(sessionId) ?? {
        speaking: false,
        lastLoudAt: 0,
        level: 0,
      };

      if (level >= START_THRESHOLD) {
        previous.lastLoudAt = now;
      }

      /*
       * A decisão, na ordem que importa.
       *
       * Estar acima do limiar de início ACENDE. Estar abaixo do de parada só
       * APAGA se já passou o tempo de retenção. Entre os dois limiares, o estado
       * anterior é mantido — é essa faixa morta que impede a piscada.
       */
      let speaking: boolean;
      if (level >= START_THRESHOLD) {
        speaking = true;
      } else if (level < STOP_THRESHOLD && now - previous.lastLoudAt > HOLD_MS) {
        speaking = false;
      } else {
        speaking = previous.speaking;
      }

      const changed = speaking !== previous.speaking;
      this.state.set(sessionId, { speaking, lastLoudAt: previous.lastLoudAt, level });

      /*
       * Reporta na mudança de ESTADO, ou quando o nível se move de forma
       * perceptível enquanto fala.
       *
       * O segundo caso existe porque a interface usa o nível para intensidade de
       * brilho. Reportar toda variação faria um `setState` a 10 Hz por par —
       * numa sala de oito, 70 atualizações por segundo na árvore React durante
       * uma chamada. O passo de 0,05 é abaixo do que se percebe num brilho e
       * corta a maior parte disso.
       */
      if (changed || (speaking && Math.abs(level - previous.level) > 0.05)) {
        changes.push({ sessionId, speaking, level });
      }
    }

    /*
     * Par que saiu da sala é ESQUECIDO, e reportado como não-falando.
     *
     * Sem isto, quem saiu no meio de uma palavra deixaria o indicador aceso para
     * sempre — e o mapa cresceria por toda a sessão, guardando sessões mortas.
     */
    for (const sessionId of [...this.state.keys()]) {
      if (seen.has(sessionId)) continue;
      const previous = this.state.get(sessionId);
      this.state.delete(sessionId);
      if (previous?.speaking === true) {
        changes.push({ sessionId, speaking: false, level: 0 });
      }
    }

    return changes;
  }

  /** Estado atual de uma sessão, sem amostrar. */
  isSpeaking(sessionId: string): boolean {
    return this.state.get(sessionId)?.speaking ?? false;
  }

  reset(): void {
    this.state.clear();
  }
}

/**
 * Extrai o maior `audioLevel` das fontes de sincronização de um receiver.
 *
 * O maior e não a média: um receiver pode ter mais de uma fonte (CSRC de um
 * misturador), e nesse caso o que interessa é se ALGUÉM naquele fluxo está
 * falando.
 */
function readLevel(receiver: RTCRtpReceiver): number {
  /*
   * `getSynchronizationSources` não existe em todo navegador.
   *
   * Firefox não implementava até recentemente. Onde falta, o nível fica em zero
   * e o indicador simplesmente não acende — degradação silenciosa e correta, e
   * NÃO um erro: uma chamada funciona perfeitamente sem saber quem fala.
   */
  if (typeof receiver.getSynchronizationSources !== 'function') return 0;

  let highest = 0;
  try {
    for (const source of receiver.getSynchronizationSources()) {
      /*
       * `audioLevel` é opcional na especificação.
       *
       * Ele só chega quando a extensão de cabeçalho RTP foi negociada — o que
       * depende do SDP dos DOIS lados. `?? 0` cobre o caso de o par não a ter
       * anunciado, em vez de produzir `NaN` numa comparação.
       */
      const level = source.audioLevel ?? 0;
      if (level > highest) highest = level;
    }
  } catch {
    /*
     * Lança se o receiver já foi encerrado entre a montagem da lista e a
     * leitura — uma corrida real quando alguém sai da chamada. Nível zero é a
     * resposta certa para um receiver morto.
     */
    return 0;
  }
  return highest;
}
